/**
 * CustomFieldsLink - Minimal text indicator with cogs icon that opens a dialog
 * to view/manage custom fields for the given entity type.
 *
 * Renders inline in listing page headers as:  "2 custom fields ⚙"
 */

// react
import { useContext, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { alpha, Stack, Tooltip, Typography, useTheme } from '@mui/material';

// icons
import { Settings as IconSettings } from '@mui/icons-material';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// types
import { CustomColumnDefinition } from '../../../@types';

// dialog
import {
	CustomFieldEntity,
	DialogCustomFields,
} from '../../dialogs/DialogCustomFields/DialogCustomFields';

// Types (re-export for convenience)

export type CustomFieldsEntity = CustomFieldEntity;

// Props

export interface CustomFieldsLinkProps {
	/** Which entity type's custom fields to show */
	entity: CustomFieldsEntity;
}

// Component

export const CustomFieldsLink = ({ entity }: CustomFieldsLinkProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { project } = useContext(ContextProject);
	const [dialogOpen, setDialogOpen] = useState(false);

	const entityTypeMap: Record<CustomFieldsEntity, string> = useMemo(
		() => ({
			activity: 'activity',
			beneficiary: 'beneficiary',
			contract: 'contract',
			spendPlan: 'spend_plan',
			supplier: 'supplier',
			workPlan: 'work_plan',
		}),
		[]
	);
	const columns = useMemo(() => {
		if (!project) return [];
		const apiEntityType = entityTypeMap[entity];
		const row = (project.customFieldDefinitions || []).find((d) => d.entityType === apiEntityType);
		return (row?.definitions as CustomColumnDefinition[] | undefined) ?? [];
	}, [project, entity, entityTypeMap]);

	const activeCount = useMemo(() => columns.filter((c) => c.isActive).length, [columns]);

	const label =
		activeCount > 0
			? t('customFields.count', { count: activeCount })
			: t('customFields');

	return (
		<>
			<Tooltip title={t('customFields.manage')} arrow>
				<Stack
					direction="row"
					alignItems="center"
					spacing={0.5}
					onClick={() => setDialogOpen(true)}
					sx={{
						cursor: 'pointer',
						borderRadius: 1,
						px: 0.75,
						py: 0.25,
						'&:hover': {
							bgcolor: alpha(theme.palette.primary.main, 0.06),
							'& .cf-label': { color: theme.palette.primary.main },
							'& .cf-icon': { color: theme.palette.primary.main },
						},
					}}>
					<Typography
						className="cf-label"
						sx={{
							color: 'text.secondary',
							fontSize: '1.25rem',
							fontWeight: 500,
							lineHeight: 1.4,
							whiteSpace: 'nowrap',
							userSelect: 'none',
						}}>
						{label}
					</Typography>
					<IconSettings className="cf-icon" sx={{ fontSize: 18, color: 'text.secondary' }} />
				</Stack>
			</Tooltip>

			<DialogCustomFields entity={entity} open={dialogOpen} onClose={() => setDialogOpen(false)} />
		</>
	);
};
